import type { BackendModule, ReadCallback } from 'i18next';

type JsonModule = { default: Record<string, unknown> };
export type LocaleLoader = () => Promise<JsonModule>;
export type LocaleLoaders = Record<string, LocaleLoader>;

/**
 * Every translation file as a lazy import. Vite emits one small chunk per `<language>/<namespace>`
 * pair, fetched only when that namespace is first needed (see `createLazyBackend`).
 */
export const localeLoaders = import.meta.glob('../locales/*/*.json') as LocaleLoaders;

/**
 * i18next backend that resolves a `<language>/<namespace>` pair to one of the lazily imported JSON
 * files. With react-i18next's `useSuspense`, a component that asks for a namespace that is not
 * loaded yet simply suspends until the chunk arrives, so route chunks stay self-contained.
 */
export function createLazyBackend(loaders: LocaleLoaders = localeLoaders): BackendModule {
  return {
    type: 'backend',
    init() {
      /* nothing to configure */
    },
    read(language: string, namespace: string, callback: ReadCallback) {
      const loader = loaders[`../locales/${language}/${namespace}.json`];
      if (!loader) {
        callback(new Error(`Missing translation file: ${language}/${namespace}`), null);
        return;
      }
      loader().then(
        (module) => callback(null, module.default),
        (error: unknown) => callback(error instanceof Error ? error : new Error(String(error)), null),
      );
    },
  };
}

/** Namespaces available for a language, derived from the translation files on disk. */
export function namespacesFor(language: string, loaders: LocaleLoaders = localeLoaders): string[] {
  const prefix = `../locales/${language}/`;
  return Object.keys(loaders)
    .filter((key) => key.startsWith(prefix))
    .map((key) => key.slice(prefix.length, -'.json'.length))
    .sort();
}
